// IDE API facade.
//
// - If a real IDE backend is configured, this can talk to it.
// - Otherwise, it falls back to a demo-mode filesystem backed by localStorage.

const API_URL = (import.meta.env.VITE_IDE_API_URL || '').replace(/\/+$/, '');

const DEMO_STORAGE_PREFIX = 'neuais.lab.ide.file.';

const DEMO_TREE = {
    name: 'workspace',
    path: 'workspace',
    is_dir: true,
    children: [
        { name: 'README.md', path: 'workspace/README.md', is_dir: false },
        { name: 'main.py', path: 'workspace/main.py', is_dir: false },
        {
            name: 'src',
            path: 'workspace/src',
            is_dir: true,
            children: [
                { name: 'app.py', path: 'workspace/src/app.py', is_dir: false },
                { name: 'utils.py', path: 'workspace/src/utils.py', is_dir: false },
                { name: 'config.json', path: 'workspace/src/config.json', is_dir: false },
            ],
        },
        {
            name: 'notebooks',
            path: 'workspace/notebooks',
            is_dir: true,
            children: [{ name: 'demo-notebook.py', path: 'workspace/notebooks/demo-notebook.py', is_dir: false }],
        },
    ],
};

const DEMO_DEFAULT_CONTENTS = {
    'workspace/README.md': `# NeuAIs Lab (Demo)\n\nThis is a **demo-mode** Lab IDE running in the browser.\n\nWhat works here:\n- Browse a sample file tree\n- Edit files\n- Save (persists to your browser via localStorage)\n\nWhat is stubbed:\n- Real containers / GPUs\n- Git + terminal backend\n\nTip: Try opening \"main.py\" and press Ctrl+S / Cmd+S to save.\n`,
    'workspace/main.py': `"""NeuAIs Lab — demo entrypoint."""\n\nfrom src.utils import greet\n\nif __name__ == "__main__":\n    print(greet("NeuAIs"))\n`,
    'workspace/src/app.py': `def run():\n    return "Hello from NeuAIs Lab demo"\n`,
    'workspace/src/utils.py': `def greet(name: str) -> str:\n    return f"Hello, {name}!"\n`,
    'workspace/src/config.json': `{"mode":"demo","features":{"editor":true,"save":true,"terminal":false}}\n`,
    'workspace/notebooks/demo-notebook.py': `# Demo notebook file\n\nprint("Notebook → IDE handoff works!")\n`,
};

function demoKey(path) {
    return `${DEMO_STORAGE_PREFIX}${path}`;
}

function demoRead(path) {
    try {
        const stored = localStorage.getItem(demoKey(path));
        if (stored !== null) return stored;
    } catch {
        // ignore
    }
    return DEMO_DEFAULT_CONTENTS[path] ?? '';
}

function demoWrite(path, content) {
    try {
        localStorage.setItem(demoKey(path), content);
    } catch {
        // ignore (private browsing / quota)
    }
}

async function tryFetch(url, init) {
    try {
        const res = await fetch(url, init);
        if (!res.ok) return null;
        return res;
    } catch {
        return null;
    }
}

export const ideApi = {
    // File Operations
    async getFileTree() {
        const base = API_URL || '';
        const res = await tryFetch(`${base}/api/files/tree`);
        if (!res) return DEMO_TREE;
        try {
            return await res.json();
        } catch {
            return DEMO_TREE;
        }
    },

    async getFileContent(path) {
        const base = API_URL || '';
        const res = await tryFetch(`${base}/api/files/content?path=${encodeURIComponent(path)}`);
        if (!res) return demoRead(path);
        return await res.text();
    },

    async saveFile(path, content) {
        const base = API_URL || '';
        const res = await tryFetch(`${base}/api/files/content?path=${encodeURIComponent(path)}`, {
            method: 'PUT',
            body: content,
        });

        // Demo fallback
        if (!res) {
            demoWrite(path, content);
            return;
        }
    },

    async createFile(path) {
        const base = API_URL || '';
        const res = await tryFetch(`${base}/api/files/create?path=${encodeURIComponent(path)}`, {
            method: 'POST',
        });
        if (!res) {
            demoWrite(path, '');
            return;
        }
    },

    async deleteFile(path) {
        const base = API_URL || '';
        const res = await tryFetch(`${base}/api/files/delete?path=${encodeURIComponent(path)}`, {
            method: 'DELETE',
        });
        if (!res) {
            try {
                localStorage.removeItem(demoKey(path));
            } catch {
                // ignore
            }
            return;
        }
    },

    // Git Operations (stubbed in demo mode)
    async getGitStatus() {
        const base = API_URL || '';
        const res = await tryFetch(`${base}/api/git/status`);
        if (!res) {
            return { demo: true, status: 'unavailable' };
        }
        return await res.json();
    },

    async getBranches() {
        const base = API_URL || '';
        const res = await tryFetch(`${base}/api/git/branches`);
        if (!res) {
            return { demo: true, branches: [] };
        }
        return await res.json();
    },

    async commit(message) {
        const base = API_URL || '';
        const res = await tryFetch(`${base}/api/git/commit`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ message }),
        });
        if (!res) {
            throw new Error('Git is not available in demo mode');
        }
    },

    async checkoutBranch(branch) {
        const base = API_URL || '';
        const res = await tryFetch(`${base}/api/git/checkout`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ branch }),
        });
        if (!res) {
            throw new Error('Git is not available in demo mode');
        }
    },

    async getDiff(file) {
        const base = API_URL || '';
        const url = file
            ? `${base}/api/git/diff?file=${encodeURIComponent(file)}`
            : `${base}/api/git/diff`;
        const res = await tryFetch(url);
        if (!res) {
            return { demo: true, diff: '' };
        }
        return await res.json();
    },

    async getLog() {
        const base = API_URL || '';
        const res = await tryFetch(`${base}/api/git/log`);
        if (!res) {
            return { demo: true, commits: [] };
        }
        return await res.json();
    },

    // Terminal WebSocket
    connectTerminal() {
        const base = API_URL;
        if (!base) {
            throw new Error('Terminal backend is not available in demo mode');
        }

        const wsUrl = base.replace(/^http/, 'ws');
        return new WebSocket(`${wsUrl}/api/terminal`);
    },
};
